import { Global } from '@emotion/react';
import type { DashboardExportPanel, DashboardExportResponse } from '@tempo-io/roadmap-dto';
import { getDashboardDisplayName } from '@tempo-io/roadmap-dto';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { LoopLogo } from 'src/common/components/icons/LoopLogo';
import { z } from 'zod';
import { ExportModeProvider } from '@/common/components/ui/swimlane/context';
import { DashboardGrid } from '@/features/dashboards/components/DashboardGrid';
import { PublishedSwimlane } from '../published/PublishedSwimlane';
import { PublishedTimeline } from '../published/PublishedTimeline';
import type {
  PublishedSwimlaneResponse as FePublishedSwimlaneResponse,
  PublishedTimelineResponse as FePublishedTimelineResponse,
} from '../published/types';
import { exportTransparentGlobalStyles } from './ExportSwimlanePage.styles';
import { getBooleanQueryParam } from './exportUtils';
import { useExportDashboard } from './useExportDashboard';

export const internalDashboardExportSearchSchema = z.object({
  token: z.string().min(1, 'Token cannot be empty'),
  title: z.coerce.string().optional(),
  branding: z.coerce.string().optional(),
  date: z.coerce.string().optional(),
  transparent: z.coerce.string().optional(),
});

export type InternalDashboardExportSearchParams = z.infer<
  typeof internalDashboardExportSearchSchema
>;

export interface ExportDashboardPageProps {
  dashboardId: number;
  searchParams: InternalDashboardExportSearchParams;
  exportMode?: 'png' | 'svg';
}

export const ExportDashboardPage = ({
  dashboardId,
  searchParams,
  exportMode = 'svg',
}: ExportDashboardPageProps) => {
  const { t } = useTranslation();
  const result = useExportDashboard({ dashboardId, token: searchParams.token });
  const transparentBackground = getBooleanQueryParam(searchParams.transparent, false);

  useEffect(() => {
    if (transparentBackground) {
      document.documentElement.classList.add('export-transparent');
    }
    return () => {
      document.documentElement.classList.remove('export-transparent');
    };
  }, [transparentBackground]);

  if (result.status === 'loading') {
    return (
      <output className="flex items-center justify-center h-screen text-xl">
        {t(($) => $.views.published.loading)}
      </output>
    );
  }

  if (result.status === 'error') {
    return (
      <div
        className="flex items-center justify-center h-screen text-xl text-text-error"
        role="alert"
      >
        {t(($) => $.views.published.errorLoading, { message: result.message })}
      </div>
    );
  }

  const showTitle = getBooleanQueryParam(searchParams.title, true);
  const showBranding = getBooleanQueryParam(searchParams.branding, true);
  const showDate = getBooleanQueryParam(searchParams.date, true);

  return (
    <ExportModeProvider mode={exportMode}>
      {transparentBackground && <Global styles={exportTransparentGlobalStyles} />}
      <ExportDashboardContent
        data={result.data}
        showTitle={showTitle}
        showBranding={showBranding}
        showDate={showDate}
      />
    </ExportModeProvider>
  );
};

const ExportDashboardContent = ({
  data,
  showTitle,
  showBranding,
  showDate,
}: {
  data: DashboardExportResponse;
  showTitle: boolean;
  showBranding: boolean;
  showDate: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const panelByPosition = new Map(data.panels.map((panel) => [panel.position, panel]));

  const renderCell = (position: number) => {
    const panel = panelByPosition.get(position);
    return (
      <div className="h-full w-full overflow-hidden" data-export-target="panel">
        {panel ? (
          <ExportPanelContent panel={panel} />
        ) : (
          <UnavailablePanel label={t(($) => $.dashboard.export.panelUnavailable.empty)} />
        )}
      </div>
    );
  };

  const showFooter = showBranding || showDate;

  return (
    <div
      className="flex flex-col w-screen min-h-screen"
      style={{ backgroundColor: 'var(--export-bg-color, white)' }}
      data-export-target="root"
    >
      {showTitle && (
        <header
          className="flex-none px-8 pt-6 pb-4"
          style={{ backgroundColor: 'var(--export-bg-color, white)' }}
          data-export-target="header"
        >
          <h1
            data-testid="dashboard-export-title"
            className="text-2xl font-bold text-text-neutral-primary"
          >
            {getDashboardDisplayName({ id: data.dashboardId, name: data.name })}
          </h1>
        </header>
      )}

      <div className="flex-1 min-h-0" data-testid="dashboard-export-grid">
        <DashboardGrid
          dashboardId={data.dashboardId}
          layout={data.layout}
          panels={data.panels}
          scrollContainer={null}
          renderCell={renderCell}
        />
      </div>

      {showFooter && (
        <footer
          className="flex-none flex items-center justify-between px-8 py-4 w-full"
          style={{ backgroundColor: 'var(--export-bg-color, white)' }}
          data-export-target="footer"
        >
          {showDate ? (
            <time
              dateTime={data.exportedAt}
              className="text-sm text-text-neutral-secondary"
              data-export-target="footer-date"
            >
              {t(($) => $.views.export.exportedOn, {
                date: new Date(data.exportedAt).toLocaleDateString(i18n.language, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                }),
              })}
            </time>
          ) : (
            <span />
          )}
          {showBranding && (
            <div className="flex items-center gap-2" data-export-target="footer-branding">
              <LoopLogo width={24} height={24} />
              <span className="text-sm font-semibold text-text-neutral-primary">
                {t(($) => $.views.export.tempoBrandingName)}
              </span>
            </div>
          )}
        </footer>
      )}
    </div>
  );
};

const ExportPanelContent = ({ panel }: { panel: DashboardExportPanel }) => {
  const { t } = useTranslation();
  const { content } = panel;

  switch (content.kind) {
    case 'swimlane': {
      const swimlane = toPublishedSwimlane(content);
      if (!swimlane) {
        return <UnavailablePanel label={t(($) => $.dashboard.export.panelUnavailable.failed)} />;
      }
      return <PublishedSwimlane data={swimlane} isHeadersSticky={false} />;
    }

    case 'timeline':
      return (
        <PublishedTimeline
          data={toPublishedTimeline(content.data)}
          searchParams={{}}
          showLegend={false}
          isExportMode
          hideLegendOverlay
        />
      );

    case 'unavailable':
      return (
        <UnavailablePanel label={t(($) => $.dashboard.export.panelUnavailable[content.reason])} />
      );
  }
};

const toPublishedSwimlane = (
  content: Extract<DashboardExportPanel['content'], { kind: 'swimlane' }>,
): FePublishedSwimlaneResponse | null => {
  const { data, viewId } = content;
  if (!data.layout) return null;
  return {
    ...data,
    layout: {
      ...data.layout,
      viewType: 'swimlane',
      viewId,
      header1: data.layout.header1 ?? '',
      header2: data.layout.header2 ?? '',
    },
    legendItems: data.legendItems ?? [],
  };
};

const toPublishedTimeline = (
  data: Extract<DashboardExportPanel['content'], { kind: 'timeline' }>['data'],
): FePublishedTimelineResponse => data;

const UnavailablePanel = ({ label }: { label: string }) => (
  <div
    className="flex h-full w-full items-center justify-center rounded-lg border border-dashed border-border-neutral text-text-neutral-secondary text-sm"
    data-testid="dashboard-export-panel-unavailable"
  >
    {label}
  </div>
);
